import React, { useEffect, useRef, useState } from "react";
import Highlighter from "react-highlight-words";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  Button,
  Col,
  Row,
  Table,
  Modal,
  Input,
  Space,
  message,
  Card,
  Statistic,
  Tabs,
} from "antd";
import { Pie } from "@ant-design/plots";
import { crmService } from "../../../services/crm.service";
import ModalForm from "./components/ModalForm";
import {
  CheckOutlined,
  CloseOutlined,
  FormOutlined,
  SearchOutlined,
  CopyOutlined,
  SendOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { mailSenderService } from "../../../services/mailSender.service";

function PageForEmbbed() {
  const [tableValue, setTableValue] = useState([]);
  const [leads, setLeads] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalOpenModify, setIsModalOpenModify] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState("");
  const [company, setCompany] = useState({ id: null });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [formSaved, setFormSaved] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [loading, setLoading] = useState(true);
  const [countConsentPrivacy, setCountConsentPrivacy] = useState([]);
  const [countConsent, setCountConsent] = useState([]);
  const searchInput = useRef(null);

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const getNestedValue = (record, dataIndex) => {
    if (!record) return undefined;
    if (Array.isArray(dataIndex)) {
      return dataIndex.reduce((obj, key) => obj?.[key], record);
    }
    if (typeof dataIndex === "string" && dataIndex.includes(".")) {
      return dataIndex.split(".").reduce((obj, key) => obj?.[key], record);
    }
    return record[dataIndex];
  };

  const formatFullName = (contact) =>
    contact ? `${contact.name ?? ""} ${contact.surname ?? ""}`.trim() : "";

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: "block",
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{
              width: 90,
            }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),

    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? "#1677ff" : undefined,
        }}
      />
    ),

    onFilter: (value, record) => {
      const val = getNestedValue(record, dataIndex);
      if (val == null) return false;
      if (typeof val === "object") {
        return formatFullName(val).toLowerCase().includes(value.toLowerCase());
      }
      return val.toString().toLowerCase().includes(value.toLowerCase());
    },
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput?.current?.select(), 100);
      }
    },

    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const nullFirstStr = (a, b) => {
    const aEmpty = a == null || a === "";
    const bEmpty = b == null || b === "";
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return -1;
    if (bEmpty) return 1;
    return a.localeCompare(b);
  };

  const nullFirstBool = (a, b) => {
    if (a == null && b == null) return 0;
    if (a == null) return -1;
    if (b == null) return 1;
    return (a ? 1 : 0) - (b ? 1 : 0);
  };

  const columns = [
    {
      title: "Codice Azienda",
      dataIndex: "code",
      key: "code",
      sorter: (a, b) => {
        if (a.code == null && b.code == null) return 0;
        if (a.code == null) return -1;
        if (b.code == null) return 1;
        return a.code - b.code;
      },
    },
    {
      title: "Azienda",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => nullFirstStr(a.name, b.name),
      ...getColumnSearchProps("name"),
      fixed: "left",
    },
    {
      title: "Contatto",
      dataIndex: "contact",
      key: "contact",
      sorter: (a, b) => nullFirstStr(formatFullName(a.contact), formatFullName(b.contact)),
      ...getColumnSearchProps("contact.name"),
      fixed: "left",
      render: (a) => <p>{formatFullName(a)}</p>,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: (a, b) => nullFirstStr(a.email, b.email),
      ...getColumnSearchProps("email"),
    },
    // {
    //   title: "Email Secondaria",
    //   dataIndex: "secondEmail",
    //   key: "secondEmail",
    //   sorter: (a, b) =>
    //     a.secondEmail != null && b.secondEmail != null
    //       ? a.secondEmail.localeCompare(b.secondEmail)
    //       : null,
    // },
    {
      title: "Telefono",
      dataIndex: "phone",
      key: "phone",
    },
    {
      title: "Città",
      dataIndex: "city",
      key: "city",
      sorter: (a, b) => nullFirstStr(a.city, b.city),
    },
    {
      title: "Consenso Privacy",
      dataIndex: "consentPrivacy",
      key: "consentPrivacy",
      filters: [
        {
          text: "SI",
          value: true,
        },
        {
          text: "NO",
          value: false,
        },
      ],
      onFilter: (value, record) => record.consentPrivacy === value,
      filterMultiple: false,
      render: (a) =>
        a === true ? (
          <CheckOutlined style={{ color: "green" }} />
        ) : (
          <CloseOutlined style={{ color: "red" }} />
        ),
      sorter: (a, b) => nullFirstBool(a.consentPrivacy, b.consentPrivacy),
    },
    // {
    //   title: "Data Consenso Privacy",
    //   dataIndex: "dateConsentPrivacy",
    //   key: "dateConsentPrivacy",
    //   render: (a) => a?.split("T")[0],
    //   sorter: (a, b) => a.DateConsentPrivacy - b.DateConsentPrivacy,
    // },
    {
      title: "Consenso Marketing",
      dataIndex: "consentMailingList",
      key: "consentMailingList",
      render: (a) =>
        a ? (
          <CheckOutlined style={{ color: "green" }} />
        ) : (
          <CloseOutlined style={{ color: "red" }} />
        ),
      filters: [
        {
          text: "SI",
          value: true,
        },
        {
          text: "NO",
          value: false,
        },
      ],
      onFilter: (value, record) => record.consentMailingList === value,
      filterMultiple: false,
      sorter: (a, b) => nullFirstBool(a.consentMailingList, b.consentMailingList),
    },
    // {
    //   title: "Data Consenso Mailing List",
    //   dataIndex: "dateConsentMailingList",
    //   key: "dateConsentMailingList",
    //   render: (a) => a?.split("T")[0],
    //   sorter: (a, b) => a.dateConsentMailingList - b.dateConsentMailingList,
    // },
    // {
    //   title: "Consenso Call",
    //   dataIndex: "consentCall",
    //   key: "consentCall",
    //   filters: [
    //     {
    //       text: "SI",
    //       value: true,
    //     },
    //     {
    //       text: "NO",
    //       value: false,
    //     },
    //   ],
    //   onFilter: (value, record) => record.consentCall === value,
    //   filterMultiple: false,
    //   render: (a) =>
    //     a === true ? (
    //       <CheckOutlined style={{ color: "green" }} />
    //     ) : (
    //       <CloseOutlined style={{ color: "red" }} />
    //     ),
    //   sorter: (a, b) => a.consentCall - b.consentCall,
    // },
    // {
    //   title: "Motivo eliminazione ML",
    //   dataIndex: "unsubscribeDetail",
    //   key: "unsubscribeDetail",
    //   sorter: (a, b) => a.unsubscribeDetail - b.unsubscribeDetail,
    // },
    // {
    //   title: "Data Consenso Call",
    //   dataIndex: "dateConsentCall",
    //   key: "dateConsentCall",
    //   render: (a) => a?.split("T")[0],
    //   sorter: (a, b) => a.dateConsentCall - b.dateConsentCall,
    // },
    {
      title: "Agente",
      dataIndex: "membersMongoModel",
      key: "membersMongoModel",
      sorter: (a, b) => nullFirstStr(a.membersMongoModel?.name, b.membersMongoModel?.name),
      ...getColumnSearchProps("membersMongoModel"),
      render: (a) => <p>{formatFullName(a)}</p>,
    },
    {
      title: "Azioni",
      dataIndex: "",
      key: "x",
      render: (a, b, c) => (
        <Row gutter={3}>
          {/* <Col span={12}>
            <Button
              disabled={!a.consentMailingList}
              style={a.sended && { backgroundColor: "#58D909" }}
              type={a.sended ? "primary" : "default"}
              icon={<MailOutlined />}
              onClick={() => {
                generateUrl(a);
              }}
            />
          </Col> */}
          <Col span={12}>
            <Button
              type="default"
              icon={<FormOutlined />}
              onClick={() => showModalModify(a)}
            />
          </Col>
        </Row>
      ),
      fixed: "right",
    },
  ];

  const leadsColumns = [
    {
      title: "Azienda",
      dataIndex: "companyName",
      key: "companyName",
      sorter: (a, b) => nullFirstStr(a.companyName, b.companyName),
      ...getColumnSearchProps("companyName"),
    },
    {
      title: "Contatto",
      dataIndex: "",
      key: "",
      sorter: (a, b) => nullFirstStr(a.name, b.name),
      ...getColumnSearchProps("name"),
      fixed: "left",
      render: (a) => <p>{formatFullName(a)}</p>,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: (a, b) => nullFirstStr(a.email, b.email),
      ...getColumnSearchProps("email"),
    },
    {
      title: "Telefono",
      dataIndex: "phone",
      key: "phone",
    },
    {
      title: "Brand",
      dataIndex: "brand",
      key: "brand",
    },
    {
      title: "fase",
      dataIndex: "phase",
      key: "phase",
    },
    {
      title: "Consenso Privacy",
      dataIndex: "leadPrivacy",
      key: "leadPrivacy",
      render: (a) =>
        a === true ? (
          <CheckOutlined style={{ color: "green" }} />
        ) : (
          <CloseOutlined style={{ color: "red" }} />
        ),
      filters: [
        { text: "SI", value: true },
        { text: "NO", value: false },
      ],
      onFilter: (value, record) => record.leadPrivacy === value,
      filterMultiple: false,
      sorter: (a, b) => nullFirstBool(a.leadPrivacy, b.leadPrivacy),
    },
    {
      title: "Consenso Marketing",
      dataIndex: "leadMarketing",
      key: "leadMarketing",
      render: (a) =>
        a === true ? (
          <CheckOutlined style={{ color: "green" }} />
        ) : (
          <CloseOutlined style={{ color: "red" }} />
        ),
      filters: [
        { text: "SI", value: true },
        { text: "NO", value: false },
      ],
      onFilter: (value, record) => record.leadMarketing === value,
      filterMultiple: false,
      sorter: (a, b) => nullFirstBool(a.leadMarketing, b.leadMarketing),
    },
    {
      title: "Agente",
      dataIndex: "membersMongoModel",
      key: "membersMongoModel",
      sorter: (a, b) => nullFirstStr(a.membersMongoModel?.name, b.membersMongoModel?.name),
      ...getColumnSearchProps("membersMongoModel"),
      render: (a) => formatFullName(a),
    },
  ];

  useEffect(() => {
    getTable();
    getLeads();
  }, [formSaved]);

  function getTable() {
    crmService.getCompanyNotPec().then((res) => {
      setTableValue(res);
      setFormSaved(false);
      setLoading(false);
    });
  }

  function getLeads() {
    crmService.getLead().then(
      (res) => {
        const filtered = res.filter((lead) => lead?.phase !== "Cessati" && lead?.phase !== "Sospesi" && lead?.phase !== "Non qualificato");
        setLeads(filtered ?? []);
      },
      (error) => {
        message.error("Errore nel recupero dei lead");
      }
    );
  }

  function sendEmail() {
    setIsModalOpen(false);
    mailSenderService.sendTemplateEmails([company]).then((res) => {
      message.success("Email Inviata");
      crmService.updateSent(res).then(() => {
        getTable();
      });
    });
  }

  function showModal() {
    setIsModalOpen(true);
  }

  function handleCancel() {
    setIsCopied(false);
    setIsModalOpen(false);
  }

  function showModalModify(company) {
    setCompany(company);
    setIsModalOpenModify(true);
  }

  function handleOkModify(company) {
    crmService.update(company).then(() => {
      setFormSaved(true);
    });
    setIsModalOpenModify(false);
  }

  function handleCancelModify() {
    setIsModalOpenModify(false);
  }

  function getDate() {
    const today = new Date();
    const month = today.getMonth() + 1;
    const year = today.getFullYear();
    const date = today.getDate();
    return `${date}_${month}_${year}`;
  }

  function exportCompaniesToCSV() {
    const data = tableValue.map((company) => ({
      "Codice Azienda": company.code,
      Azienda: company.name,
      Contatto: formatFullName(company.contact),
      Email: company.email,
      Telefono: company.phone,
      Città: company.city,
      "Consenso Privacy": company.consentPrivacy ? "SI" : "NO",
      "Consenso Marketing": company.consentMailingList ? "SI" : "NO",
      Agente: formatFullName(company.membersMongoModel),
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Aziende");

    const csvBuffer = XLSX.write(workbook, {
      bookType: "csv",
      type: "array",
    });
    const blob = new Blob([csvBuffer], {
      type: "text/csv;charset=UTF-8",
    });

    saveAs(blob, `consensi_aziende_${getDate()}.csv`);
  }

  function exportLeadsToCSV() {
    const data = leads.map((lead) => ({
      Azienda: lead.companyName,
      Contatto: formatFullName(lead),
      Email: lead.email,
      Telefono: lead.phone,
      Brand: lead.brand,
      Fase: lead.phase,
      "Consenso Privacy": lead.leadPrivacy ? "SI" : "NO",
      "Consenso Marketing": lead.leadMarketing ? "SI" : "NO",
      Agente: formatFullName(lead.membersMongoModel),
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Lead");

    const csvBuffer = XLSX.write(workbook, {
      bookType: "csv",
      type: "array",
    });
    const blob = new Blob([csvBuffer], {
      type: "text/csv;charset=UTF-8",
    });

    saveAs(blob, `consensi_lead_${getDate()}.csv`);
  }

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    selections: [
      Table.SELECTION_ALL,
      Table.SELECTION_INVERT,
      Table.SELECTION_NONE,
    ],
  };

  function onSelectChange(newSelectedRowKeys) {
    setSelectedRowKeys(newSelectedRowKeys);
  }

  useEffect(() => {
    getConsentML();
    getConsentPrivacy();
  }, [tableValue]);

  function getConsentPrivacy() {
    let trues = 0;
    let falses = 0;
    tableValue.forEach((element) => {
      if (element.consentPrivacy) {
        trues = trues + 1;
      } else {
        falses = falses + 1;
      }
    });
    setCountConsentPrivacy([
      {
        type: "Accettato",
        value: trues,
      },
      {
        type: "Non Accettato",
        value: falses,
      },
    ]);
  }

  function getConsentML() {
    let trues = 0;
    let falses = 0;
    tableValue.forEach((element) => {
      if (element.consentMailingList) {
        trues = trues + 1;
      } else {
        falses = falses + 1;
      }
    });
    setCountConsent([
      {
        type: "Accettati",
        value: trues,
      },
      {
        type: "Non Accettati",
        value: falses,
      },
    ]);
  }

  const configPrivacy = {
    title: "Consenso Privacy",
    data: [
      { type: "Accettato", value: countConsentPrivacy[0]?.value },
      { type: "Non Accettato", value: countConsentPrivacy[1]?.value },
    ],
    angleField: "value",
    colorField: "type",
    label: {
      text: (d) => `${d.type}\n${d.value}`,
      textBaseline: "outside",
    },
    legend: { color: { title: false, position: "right" } },
  };

  const configMarketing = {
    title: "Consenso Marketing",
    data: [
      { type: "Accettato", value: countConsent[0]?.value },
      { type: "Non Accettato", value: countConsent[1]?.value },
    ],
    angleField: "value",
    colorField: "type",
    label: {
      text: (d) => `${d.type}\n${d.value}`,
      textBaseline: "outside",
    },
    legend: { color: { title: false, position: "right" } },
  };

  return (
    <>
      <Row gutter={16} style={{ marginBottom: 50 }}>
        <Col span={8} style={{ height: "30vh" }}>
          <Pie {...configPrivacy} autoFit />
        </Col>
        <Col span={8} style={{ height: "30vh" }}>
          <Pie {...configMarketing} autoFit />
        </Col>
        <Col span={8} style={{ padding: 10, height: "30vh" }}>
          <Row style={{ height: "10vh" }}>
            <Col span={24}>
              <Statistic
                style={{ width: "100%", height: "100%" }}
                title="Totale aziende"
                groupSeparator="."
                decimalSeparator=","
                value={tableValue ? tableValue?.length : 0}
              />
            </Col>
          </Row>
          <Row style={{ height: "10vh" }}>
            <Col span={24}>
              <Statistic
                style={{ width: "100%", height: "100%" }}
                title="Totale aziende che hanno accettato la privacy"
                groupSeparator="."
                decimalSeparator=","
                valueStyle={{
                  color: "#3f8600",
                }}
                value={countConsentPrivacy[0]?.value}
              />
            </Col>
          </Row>
          <Row style={{ height: "10vh" }}>
            <Col span={24}>
              <Statistic
                style={{ width: "100%", height: "100%" }}
                title="Totale aziende che hanno rifiutato la privacy  "
                groupSeparator="."
                decimalSeparator=","
                valueStyle={{
                  color: "#cf1322",
                }}
                value={countConsentPrivacy[1]?.value}
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <Row>
        <Col></Col>
      </Row>
      <Tabs
        defaultActiveKey="companies"
        items={[
          {
            key: "companies",
            label: "Aziende",
            children: (
              <Card style={{ width: "100wh" }} size="small">
                <Row justify="end" style={{ marginBottom: 8 }}>
                  <Col>
                    <Button
                      icon={<DownloadOutlined />}
                      onClick={exportCompaniesToCSV}
                    >
                      Esporta
                    </Button>
                  </Col>
                </Row>
                <Table
                  loading={loading}
                  size="small"
                  rowSelection={rowSelection}
                  rowKey="id"
                  columns={columns}
                  dataSource={tableValue}
                  pagination={{ defaultCurrent: 1 }}
                  scroll={{ x: "max-content" }}
                />
              </Card>
            ),
          },
          {
            key: "leads",
            label: "Lead",
            children: (
              <Card style={{ width: "100wh" }} size="small">
                <Row justify="end" style={{ marginBottom: 8 }}>
                  <Col>
                    <Button
                      icon={<DownloadOutlined />}
                      onClick={exportLeadsToCSV}
                    >
                      Esporta
                    </Button>
                  </Col>
                </Row>
                <Table
                  loading={loading}
                  size="small"
                  rowKey="id"
                  columns={leadsColumns}
                  dataSource={leads}
                  pagination={{ defaultCurrent: 1 }}
                  scroll={{ x: "max-content" }}
                />
              </Card>
            ),
          },
        ]}
      />
      <Modal
        title={"Invia email a " + company.name}
        open={isModalOpen}
        onCancel={handleCancel}
        cancelText={"Chiudi"}
        footer={(_, { CancelBtn }) => (
          <>
            <CancelBtn />
            <Button
              icon={<CopyOutlined />}
              type="default"
              onClick={() => {
                navigator.clipboard.writeText(generatedUrl);
                setIsCopied(true);
              }}
            >
              {isCopied ? "Copiato" : "Copia"}
            </Button>
            <Button
              icon={<SendOutlined />}
              type="primary"
              onClick={() => {
                sendEmail();
              }}
            >
              Invia E-mail
            </Button>
          </>
        )}
      >
        <p>Copia l'url:</p>
        <p style={{ margin: "16px 0" }}>{generatedUrl}</p>
        <p>Oppure "Invia E-mail" al mittente</p>
      </Modal>
      <ModalForm
        open={isModalOpenModify}
        onOk={(company) => handleOkModify(company)}
        onCancel={handleCancelModify}
        companyId={company.id}
      />
    </>
  );
}
export default PageForEmbbed;
